import { useAtomValue } from 'jotai';
import { useMemo, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSearchParams } from 'react-router';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { CaptableType } from '../../../data-models/captable2.data-model';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  captableAsOfDateStateJ,
  companyCaptablesByIdStateJ,
} from '../../../services/state/CapTableDataStateJ';
import {
  clientInvestmentsStateJ,
  otherInvestmentsStateJ,
  shareClassesStateJ,
} from '../../../services/state/CapTableFormStateJ';
import { toCaptableDataModel, createCaptableViewModel } from '../../../view-models/captable.view-model';

import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';
import { useCaptableActions } from './CapTableActions';
import { CaptableIdParam } from './useSyncCaptableFormFromPath';

export function useCreateOrUpdateCaptable() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();
  const asOfDate = useAtomValue(captableAsOfDateStateJ(companyId));
  const { updateCaptableHandler, createCaptableHandler } = useCaptableActions();
  const { pushErrorToast } = useToastMessageState();

  const [search] = useSearchParams();
  const editedCaptableId = parseInt(search.get(CaptableIdParam) ?? '');

  const shareClasses = useAtomValue(shareClassesStateJ);
  const clientInvestments = useAtomValue(clientInvestmentsStateJ);
  const otherInvestments = useAtomValue(otherInvestmentsStateJ);
  const captableMap = useAtomValue(companyCaptablesByIdStateJ(companyId));

  const captablePayload = useMemo(() => {
    const existing = captableMap.get(editedCaptableId ?? -1);
    const base = asOfCapTables
      ? existing
        ? { ...existing, date: asOfDate }
        : { companyId, currencyId: 1, type: CaptableType.user, date: asOfDate }
      : (existing ?? { companyId, currencyId: 1, type: CaptableType.user, date: new Date().toISOString() });

    return toCaptableDataModel(
      createCaptableViewModel({
        ...base,
        _viewModel: {
          shareClasses,
          clientInvestments,
          otherInvestments,
        },
      })
    );
  }, [
    captableMap,
    clientInvestments,
    companyId,
    editedCaptableId,
    otherInvestments,
    shareClasses,
    asOfDate,
    asOfCapTables,
  ]);

  const createOrUpdateCaptable = useCallback(
    async (onSuccess?: () => void) => {
      let success = false;
      try {
        const data = editedCaptableId
          ? await updateCaptableHandler(captablePayload)
          : await createCaptableHandler(captablePayload);
        success = true;
        return data;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, `Failed to ${editedCaptableId ? 'update' : 'create'} cap table`);
        pushErrorToast({ message });
      } finally {
        if (success && onSuccess) {
          onSuccess();
        }
      }
    },
    [editedCaptableId, updateCaptableHandler, captablePayload, createCaptableHandler, pushErrorToast]
  );

  return { captablePayload, createOrUpdateCaptable };
}
